import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Badge, Button } from 'react-bootstrap';

import DeleteModal from 'App/components/DeleteModal';
import { stripAllHtml } from 'App/utils';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';
import withFormikFields, { fieldHelpersPropType } from 'Shared/form/withFormikFields';
import assessmentSettingsPropType from '../../types/assessmentSettings';
import questionPropType from '../types/questionPropType';
import QuestionAccordionWrapper from './QuestionAccordionWrapper';
import QuestionAnswersSection from './QuestionAnswersSection';
import QuestionAttributesSection from './QuestionAttributesSection';
import QuestionTextSection from './QuestionTextSection';

const AssessmentManagerQuestionStyle = styled.div`
    margin-bottom: 2em;

    .accordion-wrapper {
        padding-top: 0;
    }

    .assessment-manager-question-header {
        flex-grow: 1;

        button {
            display: flex;
            flex-grow: 1;
            align-items: baseline;
            text-align: left;
            font-size: 1.4em;
            margin-top: 0;
            margin-bottom: 0.5em;
        }
    }

    .assessment-manager-question-section-header {
        button {
            font-size: 1.2em;
            margin: 0;
        }
    }

    .accordion-body {
        padding-left: 1.8em;
    }

    .btn-danger {
        margin-top: 1em;
        margin-left: 1.8em;
    }
`;

const QuestionHeaderStyle = styled.div`
    display: flex;
    flex-grow: 1;
    justify-content: space-between;
`;

const QuestionSubheaderStyle = styled.div`
    font-size: 0.875rem;
`;

const QuestionBadgesStyle = styled.div`
    white-space: nowrap;

    .badge {
        color: var(--biscay);
        background-color: white;
        border: 1px solid var(--biscay);
        margin-left: 0.25em;
    }
`;

const AssessmentManagerQuestion = ({ questionId, questionIndex, assessment, fieldValues, fieldHelpers }) => {
    const question = fieldValues[`questions.${questionId}`];
    const { setValue } = fieldHelpers[`questions.${questionId}`];
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const isClinicalAdmin = useIsPermitted(PERMISSION_TYPES.CLINICAL_ADMIN);

    if (question.deleted) {
        return null;
    }

    return (
        <AssessmentManagerQuestionStyle>
            <QuestionAccordionWrapper
                questionId={questionId}
                statePath="main"
                title={
                    <QuestionHeaderStyle>
                        <div>
                            <div>
                                Question #{questionIndex + 1} - {question.type}
                            </div>
                            <QuestionSubheaderStyle>
                                {stripAllHtml(
                                    question.question?.replace(/<\/(p|h\d|li|td|th)>/g, ' ').replace(/<br>/g, ' ')
                                )}
                            </QuestionSubheaderStyle>
                        </div>
                        <QuestionBadgesStyle>
                            {question.attributes.hidden && <Badge>Hidden</Badge>}
                            {question.attributes.is_mobile && <Badge>Mobile</Badge>}
                            {question.attributes.required &&
                                !isClinicalAdmin &&
                                !question.attributes.therigy_required && <Badge>Required</Badge>}
                            {(question.attributes.therigy_required ||
                                (isClinicalAdmin && question.attributes.required)) && <Badge>Therigy Required</Badge>}
                            {question.attributes.ehr_encounter_note && <Badge>EHR</Badge>}
                            {question.attributes.scoring_enabled && <Badge>Scoring</Badge>}
                        </QuestionBadgesStyle>
                    </QuestionHeaderStyle>
                }
                headerClassName="assessment-manager-question-header"
            >
                <div>
                    <QuestionAttributesSection question={question} assessment={assessment} />
                    <QuestionTextSection question={question} />
                    {['select', 'radio', 'multiselect', 'checkbox'].includes(question.type) && (
                        <QuestionAnswersSection question={question} />
                    )}

                    {question.can_delete && (
                        <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                            Delete Question
                        </Button>
                    )}
                    {showDeleteModal && (
                        <DeleteModal
                            show
                            title="Delete Question"
                            message={
                                <div>
                                    <p>Are you sure you want to delete this question?</p>
                                    <p>User responses will be unlinked and sub-questions will also be deleted.</p>
                                </div>
                            }
                            onHide={() => setShowDeleteModal(false)}
                            onDelete={() => {
                                setValue({ ...question, deleted: true });
                                setShowDeleteModal(false);
                            }}
                        />
                    )}
                </div>
            </QuestionAccordionWrapper>
        </AssessmentManagerQuestionStyle>
    );
};

AssessmentManagerQuestion.propTypes = {
    questionId: PropTypes.string.isRequired,
    questionIndex: PropTypes.number.isRequired,
    assessment: assessmentSettingsPropType.isRequired,
    fieldValues: PropTypes.objectOf(questionPropType).isRequired,
    fieldHelpers: fieldHelpersPropType.isRequired,
};

export default withFormikFields({
    WrappedComponent: AssessmentManagerQuestion,
    fieldNames: [({ questionId }) => `questions.${questionId}`],
});
